import React, { useCallback, useEffect, useState } from "react";
import { Button, Modal } from "antd";
import { inject, observer } from "mobx-react";
import { useForm } from "antd/es/form/Form";
import ee from "../../../services/EventEmitter";
import { ENABLE_UPDATE_CMR_FORM } from "../../../constants/events";
import UpdateCmrStore from "../../../stores/cmr/UpdateCmrStore";
import Spinner from "../../common/Spinner";
import { ErrorValueType } from "../../../stores/cmr/CreateCmrStore";
import CmrForm from "./form";
import {
  SAVE_CMR_DOWNLOAD_PDF,
  UPDATE_CMR,
} from "../../../constants/translates";
import { IAppStore } from "../../../stores/Interfaces";
import FormFooter from "./footer";
import {
  CANCEL_BUTTON,
  COPY_TO_NEW_BUTTON,
  SAVE_BUTTON,
} from "../../../constants/buttons";
import initialValues from "../../../normalizer/cmrs/initialValues";

interface Props {
  store?: IAppStore;
}

const updateStore = new UpdateCmrStore();

const UpdateCmr: React.FC<Props> = ({ store }) => {
  const [form] = useForm();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    ee.on(ENABLE_UPDATE_CMR_FORM, (cmrId) => {
      setVisible(true);
      updateStore.loadCmr(cmrId).finally(() => {
        form.resetFields();
      });
    });
  }, [form]);

  const hideModal = useCallback(() => {
    updateStore.resetCmr();
    setVisible(false);
  }, []);

  const onFinishHandler = useCallback(
    (values: Object) => {
      updateStore.update(updateStore.cmr?.id, values).finally(() => {
        hideModal();
      });
    },
    [hideModal]
  );

  const onSaveAndDownloadHandler = useCallback(() => {
    updateStore.saveAndDownload(
      updateStore.cmr?.id,
      form.getFieldsValue() as Object
    );
  }, [form]);

  const onCopyToNewHandler = useCallback(() => {
    updateStore.copyToNew(updateStore.cmr?.id).then((id: unknown) => {
      if (!id) {
        return;
      }

      new Promise<void>((resolve) => {
        hideModal();
        resolve();
      }).finally(() => {
        ee.emit(ENABLE_UPDATE_CMR_FORM, id);
      });
    });
  }, [hideModal]);

  const onCancelHandler = useCallback(() => {
    hideModal();
  }, [hideModal]);

  const footer = (
    <FormFooter
      elements={[
        {
          key: "save",
          element: (
            <Button type="primary" htmlType="submit">
              {SAVE_BUTTON}
            </Button>
          ),
        },

        {
          key: "cancel",
          element: (
            <Button type="default" htmlType="button" onClick={onCancelHandler}>
              {CANCEL_BUTTON}
            </Button>
          ),
        },

        {
          key: "copyToNew",
          element: (
            <Button
              type="primary"
              htmlType="button"
              onClick={onCopyToNewHandler}
            >
              {COPY_TO_NEW_BUTTON}
            </Button>
          ),
        },

        {
          key: "savAndDownload",
          element: (
            <Button
              type="primary"
              htmlType="button"
              onClick={onSaveAndDownloadHandler}
            >
              {SAVE_CMR_DOWNLOAD_PDF}
            </Button>
          ),
        },
      ]}
    />
  );

  return (
    <Modal
      visible={visible}
      onCancel={onCancelHandler}
      title={UPDATE_CMR}
      footer={null}
      className="xl"
    >
      {!updateStore.cmr ? (
        <Spinner isActive />
      ) : (
        <CmrForm
          form={form}
          onCancel={onCancelHandler}
          onFinish={onFinishHandler}
          onSaveAndDownloadPdf={onSaveAndDownloadHandler}
          initialValues={initialValues(
            updateStore.cmr,
            store?.settingsStore.clients || [],
            store?.settingsStore.departments || []
          )}
          errors={updateStore.errors as { [key: string]: ErrorValueType }}
          countries={store?.countryStore.asOptions}
          clients={store?.settingsStore.clients}
          departments={store?.settingsStore.departments}
          footer={footer}
        />
      )}
    </Modal>
  );
};
export default inject("store")(observer(UpdateCmr));
